/**
 * usePlayMutations Hook
 *
 * Provides mutation handlers for play blocks with undo/redo support.
 * - Player mutations (add, update, delete)
 * - Drawing mutations (add, update, delete)
 * - Play metadata mutations (name, notes, hash position)
 * - Formation/Concept reference mutations
 *
 * All mutations use useUndoableMutation to support undo/redo.
 *
 * PERFORMANCE OPTIMIZATION:
 * Uses cached ResolvedBlock data from React Query instead of fetching
 * before each mutation. This eliminates the pre-mutation fetch pattern,
 * reducing HTTP requests by ~50% for mutation operations.
 */

import { useCallback } from 'react'
import type { PlayBlockMutations } from './Types'
import { useUndo } from './undo'
import type { Player } from '@/types/Player.types'
import type { Drawing } from './Types'
import type { HashAlignment } from './Types'
import type {
	ResolvedBlock,
	PlayerBlock,
	ResolvedDrawing,
	ConceptReferenceBlock,
	PlayBlock_Union,
} from '@/types/blocks'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'
import { drawingToCreatePayload } from '@/adapters/DrawingAdapter'
import type { PlayCallChip } from '@/types/BlockProperties'

// ========== API Base URL ==========

function getApiBaseUrl(): string {
	if (typeof window !== 'undefined' && window.location?.origin) {
		return window.location.origin
	}
	return ''
}

// ========== Cache Lookup Helpers ==========

/**
 * Find a player block from cached ResolvedBlock data.
 * Returns undefined if not found.
 */
function findCachedPlayer(
	resolved: ResolvedBlock | null,
	playerId: string
): PlayerBlock | undefined {
	if (!resolved) return undefined
	return resolved.players.find((p) => p.id === playerId)
}

/**
 * Find a drawing block with children from cached ResolvedBlock data.
 * Returns undefined if not found.
 */
function findCachedDrawing(
	resolved: ResolvedBlock | null,
	drawingId: string
): ResolvedDrawing | undefined {
	if (!resolved) return undefined
	return resolved.drawings.find((d) => d.drawing.id === drawingId)
}

/**
 * Find a concept reference block from cached ResolvedBlock data.
 * Returns undefined if not found.
 */
function findCachedConceptReference(
	resolved: ResolvedBlock | null,
	referenceId: string
): { reference: ConceptReferenceBlock; conceptId: string } | undefined {
	if (!resolved) return undefined
	const ref = resolved.conceptReferences.find((cr) => cr.reference.id === referenceId)
	if (!ref) return undefined
	return { reference: ref.reference, conceptId: ref.concept.id }
}

/** Play-specific properties from a ResolvedBlock's play. */
interface PlayProperties {
	name?: string
	notes?: string | null
	hashPosition?: string
	callChips?: PlayCallChip[]
}

/** Extract play properties from a cached ResolvedBlock. */
function getPlayProperties(resolved: ResolvedBlock | null): PlayProperties {
	if (!resolved || resolved.block.type !== 'play') return {}
	return resolved.block.properties as PlayProperties
}

/**
 * Convert ResolvedDrawing to a flat block structure for undo.
 * Flattens the drawing, control points, and markers.
 * Note: Path segments are now stored in block_references, not as child blocks.
 */
function flattenDrawingChildren(drawing: ResolvedDrawing): PlayBlock_Union[] {
	const children: PlayBlock_Union[] = []

	// Add control points directly (no wrapper type)
	for (const cp of drawing.controlPoints) {
		children.push(cp)
	}

	// Add pre-snap markers
	children.push(...drawing.preSnapMarkers)

	return children
}

// ========== Hook Options ==========

/**
 * Options for usePlayMutations hook
 */
export interface UsePlayMutationsOptions {
	playBlockId: string | null
	playbookId: string
	/**
	 * Getter for cached ResolvedBlock data.
	 * Called at mutation time to get current cached state for undo operations.
	 * Using a getter ensures we always access the latest cached data.
	 */
	getCachedPlay: () => ResolvedBlock | null
}

// ========== Hook ==========

/**
 * Hook for play block mutations with undo/redo support
 *
 * @param options - Hook options including playBlockId, playbookId, and getCachedPlay
 * @returns Mutation handlers with undo support
 */
export function usePlayMutations(options: UsePlayMutationsOptions): PlayBlockMutations {
	const { playBlockId, getCachedPlay } = options
	const { pushOperation, currentTransactionId } = useUndo()
	const baseUrl = getApiBaseUrl()

	// ========== Player Mutations ==========

	const addPlayer = useCallback(
		async (player: Player): Promise<string> => {
			if (!playBlockId) return player.id

			// Make API call - spatial data goes in properties (Phase 6 migration)
			const response = await secureJsonFetch(`${baseUrl}/api/plays/${playBlockId}/blocks`, {
				method: 'POST',
				body: {
					type: 'player',
					parent_block_id: playBlockId,
					player_label: player.label,
					player_color: player.color,
					properties: { x: player.x, y: player.y },
					player_is_lineman: player.isLineman,
					player_is_ghost: false,
				},
			})

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(`Failed to add player: ${response.status} - ${errorText}`)
			}
			const created = await response.json()

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'create_block',
				label: `Add Player ${player.label}`,
				inverseData: {
					type: 'create_block',
					blockId: created.id,
				},
				redoData: {
					type: 'create_block',
					block: created,
					children: [],
					references: [],
				},
				transactionId: currentTransactionId ?? undefined,
			})

			// Return the server-assigned ID so caller can update selectedPlayerId
			return created.id
		},
		[playBlockId, baseUrl, pushOperation, currentTransactionId]
	)

	const updatePlayer = useCallback(
		async (playerId: string, updates: Partial<Player>) => {
			// PERFORMANCE: Get original values from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const cachedPlayer = findCachedPlayer(cachedPlay, playerId)

			if (!cachedPlayer) {
				throw new Error(`Player ${playerId} not found in cache`)
			}

			// Build update payload - all player data goes in properties JSONB
			const propsUpdate: Record<string, unknown> = {}
			if (updates.label !== undefined) propsUpdate.label = updates.label
			if (updates.color !== undefined) propsUpdate.color = updates.color
			if (updates.isLineman !== undefined) propsUpdate.isLineman = updates.isLineman
			if (updates.x !== undefined) propsUpdate.x = updates.x
			if (updates.y !== undefined) propsUpdate.y = updates.y

			const updatePayload: Record<string, unknown> = { properties: propsUpdate }

			// Make API call
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${playerId}/update`, {
				method: 'PATCH',
				body: updatePayload,
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update player ${playerId}: ${updateResponse.status} - ${errorText}`)
			}

			// Build original values for undo from cached data
			const origProps: Record<string, unknown> = {}
			if (updates.label !== undefined) origProps.label = cachedPlayer.properties.label
			if (updates.color !== undefined) origProps.color = cachedPlayer.properties.color
			if (updates.isLineman !== undefined) origProps.isLineman = cachedPlayer.properties.isLineman
			const cachedSpatial = cachedPlayer.properties as { x?: number; y?: number }
			if (updates.x !== undefined) origProps.x = cachedSpatial.x
			if (updates.y !== undefined) origProps.y = cachedSpatial.y

			const originalValues: Record<string, unknown> = { properties: origProps }

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Update Player',
				inverseData: {
					type: 'update_block',
					blockId: playerId,
					originalValues,
				},
				redoData: {
					type: 'update_block',
					blockId: playerId,
					newValues: updatePayload,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	const deletePlayer = useCallback(
		async (playerId: string) => {
			// PERFORMANCE: Get player data from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const cachedPlayer = findCachedPlayer(cachedPlay, playerId)

			if (!cachedPlayer) {
				throw new Error(`Player ${playerId} not found in cache`)
			}

			// Make API call
			const deleteResponse = await secureFetch(`${baseUrl}/api/blocks/${playerId}/delete`, {
				method: 'DELETE',
			})

			if (!deleteResponse.ok) {
				const errorText = await deleteResponse.text()
				throw new Error(`Failed to delete player ${playerId}: ${deleteResponse.status} - ${errorText}`)
			}

			// Push to undo stack using cached data
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'delete_block',
				label: `Delete Player ${cachedPlayer.properties.label}`,
				inverseData: {
					type: 'delete_block',
					block: cachedPlayer,
					children: [],
					references: [],
				},
				redoData: {
					type: 'delete_block',
					blockId: playerId,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	// ========== Drawing Mutations ==========

	const addDrawing = useCallback(
		async (drawing: Drawing) => {
			if (!playBlockId) return

			const payload = drawingToCreatePayload(drawing)

			console.debug('[addDrawing] Creating drawing with %d control points and %d path connections',
				payload.controlPoints.length, payload.pathConnections.length)

			// Use bulk endpoint - creates drawing + control points + references in single request
			const response = await secureJsonFetch(`${baseUrl}/api/plays/${playBlockId}/drawings`, {
				method: 'POST',
				body: payload,
			})

			if (!response.ok) {
				const errorText = await response.text()
				console.error('[addDrawing] Bulk creation failed:', response.status, errorText)
				throw new Error(`Failed to add drawing: ${response.status} - ${errorText}`)
			}

			const result = await response.json()
			console.debug('[addDrawing] Created drawing %s with %d control points and %d references',
				result.block.id,
				result.controlPoints?.length ?? 0,
				result.references?.length ?? 0)

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'create_block',
				label: 'Add Drawing',
				inverseData: {
					type: 'create_block',
					blockId: result.block.id,
				},
				redoData: {
					type: 'create_block',
					block: result.block,
					children: [],
					references: [],
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, pushOperation, currentTransactionId]
	)

	const updateDrawing = useCallback(
		async (drawingId: string, updates: Partial<Drawing>) => {
			// PERFORMANCE: Get original values from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const cachedDrawing = findCachedDrawing(cachedPlay, drawingId)

			if (!cachedDrawing) {
				throw new Error(`Drawing ${drawingId} not found in cache`)
			}

			// Build style update from incoming changes
			const styleUpdate: Record<string, unknown> = {}
			if (updates.style?.color !== undefined) styleUpdate.color = updates.style.color
			if (updates.style?.strokeWidth !== undefined) styleUpdate.strokeWidth = updates.style.strokeWidth
			if (updates.style?.brushSize !== undefined) styleUpdate.brushSize = updates.style.brushSize
			if (updates.style?.lineStyle !== undefined) styleUpdate.lineStyle = updates.style.lineStyle
			if (updates.style?.lineEnd !== undefined) styleUpdate.lineEnd = updates.style.lineEnd
			if (updates.style?.pathMode !== undefined) styleUpdate.pathMode = updates.style.pathMode

			// Merge with current style so server replaces the full style key
			const currentStyle = cachedDrawing.drawing.properties.style ?? {}
			const mergedStyle = { ...currentStyle, ...styleUpdate }
			const updatePayload = { properties: { style: mergedStyle } }

			// Make API call using cached block's ID (always raw UUID)
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${cachedDrawing.drawing.id}/update`, {
				method: 'PATCH',
				body: updatePayload,
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update drawing ${drawingId}: ${updateResponse.status} - ${errorText}`)
			}

			// Build original style for undo from cached data
			const originalStyle = { ...currentStyle }

			// Push to undo stack using cached block's ID
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Update Drawing',
				inverseData: {
					type: 'update_block',
					blockId: cachedDrawing.drawing.id,
					originalValues: { properties: { style: originalStyle } },
				},
				redoData: {
					type: 'update_block',
					blockId: cachedDrawing.drawing.id,
					newValues: updatePayload,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	const deleteDrawing = useCallback(
		async (drawingId: string) => {
			// PERFORMANCE: Get drawing data with children from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const cachedDrawing = findCachedDrawing(cachedPlay, drawingId)

			if (!cachedDrawing) {
				throw new Error(`Drawing ${drawingId} not found in cache`)
			}

			// Make API call using cached block's ID (always raw UUID)
			const deleteResponse = await secureFetch(`${baseUrl}/api/blocks/${cachedDrawing.drawing.id}/delete`, {
				method: 'DELETE',
			})

			if (!deleteResponse.ok) {
				const errorText = await deleteResponse.text()
				throw new Error(`Failed to delete drawing ${drawingId}: ${deleteResponse.status} - ${errorText}`)
			}

			// Flatten the drawing's children for undo
			const children = flattenDrawingChildren(cachedDrawing)

			// Push to undo stack using cached block data
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'delete_block',
				label: 'Delete Drawing',
				inverseData: {
					type: 'delete_block',
					block: cachedDrawing.drawing,
					children,
					references: [],
				},
				redoData: {
					type: 'delete_block',
					blockId: cachedDrawing.drawing.id,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	// ========== Control Point Mutations ==========

	const updateControlPoint = useCallback(
		async (drawingId: string, pointId: string, x: number, y: number) => {
			// PERFORMANCE: Get original values from React Query cache
			const cachedPlay = getCachedPlay()
			const cachedDrawing = findCachedDrawing(cachedPlay, drawingId)

			if (!cachedDrawing) {
				console.warn(`[updateControlPoint] Drawing ${drawingId} not found in cache, skipping update`)
				return
			}

			// Find the control point in the drawing (IDs are raw UUIDs per Rule 6)
			const cachedPoint = cachedDrawing.controlPoints.find((cp) => cp.id === pointId)
			if (!cachedPoint) {
				console.warn(`[updateControlPoint] Point ${pointId} not found in drawing ${drawingId}`)
				return
			}

			// Use the control point ID from cache
			const controlPointId = cachedPoint.id

			// Build update payload - spatial data goes in properties
			const updatePayload = {
				properties: { x, y },
			}

			// Make API call to update the control point block
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${controlPointId}/update`, {
				method: 'PATCH',
				body: updatePayload,
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update control point ${pointId}: ${updateResponse.status} - ${errorText}`)
			}

			// Get original position for undo
			const originalX = (cachedPoint?.properties as { x?: number })?.x ?? 0
			const originalY = (cachedPoint?.properties as { y?: number })?.y ?? 0

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Move Control Point',
				inverseData: {
					type: 'update_block',
					blockId: controlPointId,
					originalValues: { properties: { x: originalX, y: originalY } },
				},
				redoData: {
					type: 'update_block',
					blockId: controlPointId,
					newValues: { properties: { x, y } },
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	// ========== Drawing-Player Link Mutations ==========

	const linkDrawingToPlayer = useCallback(
		async (drawingId: string, pointId: string, playerId: string) => {
			// Create a player_link reference from drawing to player
			const refResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${drawingId}/references`, {
				method: 'POST',
				body: {
					target_block_id: playerId,
					reference_type: 'player_link',
					display_options: { linkedPointId: pointId },
				},
			})

			if (!refResponse.ok) {
				const errorText = await refResponse.text()
				throw new Error(`Failed to link drawing to player: ${refResponse.status} - ${errorText}`)
			}

			// Also update drawing properties for fast read path
			// If this fails, roll back the reference to avoid inconsistency
			const propsResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${drawingId}/update`, {
				method: 'PATCH',
				body: {
					properties: { linkedPlayerId: playerId, linkedPointId: pointId },
				},
			})

			if (!propsResponse.ok) {
				// Roll back reference
				await secureFetch(
					`${baseUrl}/api/blocks/${drawingId}/references/player_link/${playerId}`,
					{ method: 'DELETE' }
				).catch(() => {}) // Best-effort rollback
				const errorText = await propsResponse.text()
				throw new Error(`Failed to update drawing properties: ${propsResponse.status} - ${errorText}`)
			}
		},
		[baseUrl]
	)

	const unlinkDrawingFromPlayer = useCallback(
		async (drawingId: string) => {
			// Get cached drawing to find the linked player
			const cachedPlay = getCachedPlay()
			const cachedDrawing = findCachedDrawing(cachedPlay, drawingId)

			// Determine player ID from cache or from properties on the drawing block
			let playerId = cachedDrawing?.linkedPlayer?.id
			if (!playerId) {
				// Fallback: read drawing properties from the cached block
				const drawingProps = cachedDrawing?.drawing.properties as
					{ linkedPlayerId?: string } | undefined
				playerId = drawingProps?.linkedPlayerId ?? undefined
			}

			if (playerId) {
				// Delete the player_link reference
				await secureFetch(
					`${baseUrl}/api/blocks/${drawingId}/references/player_link/${playerId}`,
					{ method: 'DELETE' }
				)
			}

			// Clear drawing properties
			await secureJsonFetch(`${baseUrl}/api/blocks/${drawingId}/update`, {
				method: 'PATCH',
				body: {
					properties: { linkedPlayerId: null, linkedPointId: null },
				},
			})
		},
		[baseUrl, getCachedPlay]
	)

	// ========== Play Metadata Mutations ==========

	const updatePlayName = useCallback(
		async (name: string) => {
			if (!playBlockId) return

			// PERFORMANCE: Get original name from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const originalName = getPlayProperties(cachedPlay).name ?? ''

			const updatePayload = { properties: { name } }

			// Make API call
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${playBlockId}/update`, {
				method: 'PATCH',
				body: updatePayload,
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update play name: ${updateResponse.status} - ${errorText}`)
			}

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Rename Play',
				inverseData: {
					type: 'update_block',
					blockId: playBlockId,
					originalValues: { properties: { name: originalName } },
				},
				redoData: {
					type: 'update_block',
					blockId: playBlockId,
					newValues: updatePayload,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	const updateCallChips = useCallback(
		async (chips: PlayCallChip[]) => {
			if (!playBlockId) return

			// Derive name from chip texts
			const derivedName = chips.map(c => c.text).join(' ') || 'Untitled Play'

			// Get original values from cache for undo
			const cachedPlay = getCachedPlay()
			const props = getPlayProperties(cachedPlay)
			const originalChips = props.callChips ?? []
			const originalName = props.name ?? ''

			const updatePayload = { properties: { callChips: chips, name: derivedName } }

			// Make API call — atomic name+chips update
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${playBlockId}/update`, {
				method: 'PATCH',
				body: updatePayload,
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update call chips: ${updateResponse.status} - ${errorText}`)
			}

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Update Play Call',
				inverseData: {
					type: 'update_block',
					blockId: playBlockId,
					originalValues: { properties: { callChips: originalChips, name: originalName } },
				},
				redoData: {
					type: 'update_block',
					blockId: playBlockId,
					newValues: updatePayload,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	const updatePlayNotes = useCallback(
		async (notes: string | null) => {
			if (!playBlockId) return

			// PERFORMANCE: Get original notes from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const originalNotes = getPlayProperties(cachedPlay).notes ?? null

			// Make API call
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${playBlockId}/update`, {
				method: 'PATCH',
				body: { play_notes: notes },
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update play notes: ${updateResponse.status} - ${errorText}`)
			}

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Update Notes',
				inverseData: {
					type: 'update_block',
					blockId: playBlockId,
					originalValues: { play_notes: originalNotes },
				},
				redoData: {
					type: 'update_block',
					blockId: playBlockId,
					newValues: { play_notes: notes },
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	const updateHashPosition = useCallback(
		async (position: HashAlignment) => {
			if (!playBlockId) return

			// PERFORMANCE: Get original position from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const originalPosition = getPlayProperties(cachedPlay).hashPosition ?? 'middle'

			// Make API call
			const updateResponse = await secureJsonFetch(`${baseUrl}/api/blocks/${playBlockId}/update`, {
				method: 'PATCH',
				body: { play_hash_position: position },
			})

			if (!updateResponse.ok) {
				const errorText = await updateResponse.text()
				throw new Error(`Failed to update hash position: ${updateResponse.status} - ${errorText}`)
			}

			// Push to undo stack
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'update_block',
				label: 'Update Hash Position',
				inverseData: {
					type: 'update_block',
					blockId: playBlockId,
					originalValues: { play_hash_position: originalPosition },
				},
				redoData: {
					type: 'update_block',
					blockId: playBlockId,
					newValues: { play_hash_position: position },
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	// ========== Formation/Concept Reference Mutations ==========

	const applyFormation = useCallback(
		async (formationId: string) => {
			if (!playBlockId) return

			console.log(`[applyFormation] POST /api/plays/${playBlockId}/formation-ref`, { formationId, playBlockId })

			// Use the dedicated formation-ref endpoint
			// The API handles replacing existing formations internally
			const response = await secureJsonFetch(`${baseUrl}/api/plays/${playBlockId}/formation-ref`, {
				method: 'POST',
				body: { formation_block_id: formationId },
			})

			if (!response.ok) {
				const errorText = await response.text()
				console.error(`[applyFormation] FAILED: ${response.status}`, { playBlockId, formationId, errorText })
				throw new Error(`Failed to apply formation ${formationId}: ${response.status} - ${errorText}`)
			}
			const result = await response.json()

			// Push to undo stack
			// API returns: { reference, createdPlayers, deletedReference?, deletedPlayers? }
			if (result.deletedReference) {
				// If replacing, undo should restore old formation
				pushOperation({
					id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
					timestamp: Date.now(),
					type: 'update_block',
					label: 'Change Formation',
					inverseData: {
						type: 'apply_formation',
						playBlockId,
						formationBlockId: result.deletedReference.target_block_id,
					},
					redoData: {
						type: 'apply_formation',
						playBlockId,
						formationBlockId: formationId,
					},
					transactionId: currentTransactionId ?? undefined,
				})
			} else {
				pushOperation({
					id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
					timestamp: Date.now(),
					type: 'create_reference',
					label: 'Apply Formation',
					inverseData: {
						type: 'remove_formation',
						playBlockId,
					},
					redoData: {
						type: 'apply_formation',
						playBlockId,
						formationBlockId: formationId,
					},
					transactionId: currentTransactionId ?? undefined,
				})
			}
		},
		[playBlockId, baseUrl, pushOperation, currentTransactionId]
	)

	const removeFormation = useCallback(
		async () => {
			if (!playBlockId) return

			// Use the dedicated formation-ref endpoint
			const response = await secureFetch(`${baseUrl}/api/plays/${playBlockId}/formation-ref`, {
				method: 'DELETE',
			})

			if (!response.ok) {
				// 404 means no formation to remove - not an error
				if (response.status === 404) return
				const errorText = await response.text()
				throw new Error(`Failed to remove formation: ${response.status} - ${errorText}`)
			}

			const result = await response.json()

			// Push to undo stack
			// API returns: { deletedReference, deletedPlayers }
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'delete_reference',
				label: 'Remove Formation',
				inverseData: {
					type: 'apply_formation',
					playBlockId,
					formationBlockId: result.deletedReference?.target_block_id,
				},
				redoData: {
					type: 'remove_formation',
					playBlockId,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, pushOperation, currentTransactionId]
	)

	const applyConcept = useCallback(
		async (conceptId: string) => {
			if (!playBlockId) return

			// Use the dedicated concept-refs endpoint
			const response = await secureJsonFetch(`${baseUrl}/api/plays/${playBlockId}/concept-refs`, {
				method: 'POST',
				body: { concept_block_id: conceptId },
			})

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(`Failed to apply concept ${conceptId}: ${response.status} - ${errorText}`)
			}
			const result = await response.json()

			// Push to undo stack
			// API returns: { referenceBlock }
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'create_reference',
				label: 'Apply Concept',
				inverseData: {
					type: 'delete_block',
					block: result.referenceBlock,
					children: [],
					references: [],
				},
				redoData: {
					type: 'apply_concept',
					playBlockId,
					conceptBlockId: conceptId,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[playBlockId, baseUrl, pushOperation, currentTransactionId]
	)


	const removeConceptReference = useCallback(
		async (referenceId: string) => {
			// PERFORMANCE: Get concept reference from React Query cache instead of fetching
			const cachedPlay = getCachedPlay()
			const cachedRef = findCachedConceptReference(cachedPlay, referenceId)

			if (!cachedRef) {
				throw new Error(`Concept reference ${referenceId} not found in cache`)
			}

			// Delete using block delete endpoint (concept_reference blocks are blocks)
			const deleteResponse = await secureFetch(`${baseUrl}/api/blocks/${referenceId}/delete`, {
				method: 'DELETE',
			})

			if (!deleteResponse.ok) {
				const errorText = await deleteResponse.text()
				throw new Error(`Failed to remove concept reference ${referenceId}: ${deleteResponse.status} - ${errorText}`)
			}

			// Push to undo stack using cached data
			// Note: parent_block_id is asserted non-null since concept references are always children of a play
			pushOperation({
				id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
				timestamp: Date.now(),
				type: 'delete_block',
				label: 'Remove Concept',
				inverseData: {
					type: 'apply_concept',
					playBlockId: cachedRef.reference.parent_block_id!,
					conceptBlockId: cachedRef.conceptId,
				},
				redoData: {
					type: 'delete_block',
					blockId: referenceId,
				},
				transactionId: currentTransactionId ?? undefined,
			})
		},
		[baseUrl, getCachedPlay, pushOperation, currentTransactionId]
	)

	return {
		addPlayer,
		updatePlayer,
		deletePlayer,
		addDrawing,
		updateDrawing,
		deleteDrawing,
		updateControlPoint,
		linkDrawingToPlayer,
		unlinkDrawingFromPlayer,
		updatePlayName,
		updateCallChips,
		updatePlayNotes,
		updateHashPosition,
		applyFormation,
		removeFormation,
		applyConcept,
		removeConceptReference,
	}
}

/**
 * Creates placeholder mutations for testing
 * @deprecated Use usePlayMutations hook instead
 */
export function createPlaceholderMutations(): PlayBlockMutations {
	const notImplemented = async () => {
		console.warn('[usePlayMutations] Not implemented yet - use usePlayMutations hook')
	}

	const notImplementedReturnsString = async (): Promise<string> => {
		console.warn('[usePlayMutations] Not implemented yet - use usePlayMutations hook')
		return ''
	}

	return {
		addPlayer: notImplementedReturnsString,
		updatePlayer: notImplemented,
		deletePlayer: notImplemented,
		addDrawing: notImplemented,
		updateDrawing: notImplemented,
		deleteDrawing: notImplemented,
		updateControlPoint: notImplemented,
		linkDrawingToPlayer: notImplemented,
		unlinkDrawingFromPlayer: notImplemented,
		updatePlayName: notImplemented,
		updateCallChips: notImplemented,
		updatePlayNotes: notImplemented,
		updateHashPosition: notImplemented,
		applyFormation: notImplemented,
		removeFormation: notImplemented,
		applyConcept: notImplemented,
		removeConceptReference: notImplemented,
	}
}
